import "./style.scss";

import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { formatMaintenanceRange } from "helpers/maintenanceTime";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";

const SOON_OFFSET = "--maintenance-soon-offset";
const DISMISS_STORAGE_KEY = "maintenanceSoonDismissed";

const readDismissedWindow = () => {
    try {
        return localStorage.getItem(DISMISS_STORAGE_KEY) ?? "";
    } catch {
        return "";
    }
};

/** Full-width notice when a maintenance starts within 48 hours. Sits above the fixed nav. */
const MaintenanceSoonBanner = () => {
    const { t } = useTranslation();
    const bannerRef = useRef<HTMLDivElement>(null);
    const soon = useAppSelector((state) => state.appMaintenance.soon);
    const unavailable = useAppSelector((state) => state.appMaintenance.unavailable);
    const [dismissedWindow, setDismissedWindow] = useState(readDismissedWindow);
    const range = soon && !unavailable ? formatMaintenanceRange(soon.startsAt, soon.endsAt) : null;
    const windowKey = soon ? `${soon.startsAt}|${soon.endsAt}` : "";
    const hidden = windowKey !== "" && dismissedWindow === windowKey;
    const customTitle = soon?.title.trim() ?? "";
    const showTitle = customTitle !== "" && customTitle !== t("Maintenance.Title");
    const lead = range ? t("Maintenance.SoonLead") : "";
    const label =
        range && !hidden ? `${lead} ${showTitle ? customTitle : ""} ${range.date} ${range.start} ${range.end}` : "";

    useLayoutEffect(() => {
        const node = bannerRef.current;
        if (!node || !label) {
            document.body.classList.remove("maintenance-soon-visible");
            document.body.style.removeProperty(SOON_OFFSET);
            return;
        }
        const applyOffset = () => {
            document.body.classList.add("maintenance-soon-visible");
            document.body.style.setProperty(SOON_OFFSET, `${node.offsetHeight}px`);
        };
        applyOffset();
        const observer = new ResizeObserver(applyOffset);
        observer.observe(node);
        return () => {
            observer.disconnect();
            document.body.classList.remove("maintenance-soon-visible");
            document.body.style.removeProperty(SOON_OFFSET);
        };
    }, [label]);

    if (!range || hidden) return null;

    const dismiss = () => {
        try {
            localStorage.setItem(DISMISS_STORAGE_KEY, windowKey);
        } catch {
            // The banner still closes for this visit when storage is blocked.
        }
        setDismissedWindow(windowKey);
    };

    return (
        <div className="maintenance-soon-banner" role="status" ref={bannerRef}>
            <span className="maintenance-soon-chip">{t("Maintenance.SoonChip")}</span>
            <p className="maintenance-soon-copy">
                <span className="maintenance-soon-lead">{lead}</span>
                {showTitle && <span>{customTitle}</span>}
                {range.sameDay && <span>{range.date}</span>}
                <span className="maintenance-soon-when">
                    {t(range.sameDay ? "Maintenance.SoonWhen" : "Maintenance.SoonWhenDays", range)}
                </span>
                <span className="maintenance-soon-zone">{t("Maintenance.TimeZone")}</span>
            </p>
            <button
                type="button"
                className="maintenance-soon-dismiss"
                onClick={dismiss}
                aria-label={t("Maintenance.SoonDismiss")}
            >
                <CloseOutlined />
            </button>
        </div>
    );
};

export default MaintenanceSoonBanner;
